import React from 'react';
import {getAwpApiBase} from '../../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../../session/awpRequestHeaders';

async function fetchEntitySummary(entityId) {
    const res = await fetch(
        `${getAwpApiBase()}/api/entities/${encodeURIComponent(entityId)}/summary`,
        {headers: getAwpClientHeaders()},
    );
    if (!res.ok) return null;
    return res.json();
}

/**
 * Load KB summary metadata (entity type, image) for entities on the chart.
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @returns {{ summariesById: Record<string, { entityType?: string|null, image?: string|null }>, loading: boolean }}
 */
export function useChartEntitySummaries(chartEntities, {enabled = true} = {}) {
    const [summariesById, setSummariesById] = React.useState({});
    const [loading, setLoading] = React.useState(false);

    const entityIdsKey = React.useMemo(
        () => chartEntities.map((entity) => entity.wikidataId).sort().join('\0'),
        [chartEntities],
    );

    React.useEffect(() => {
        if (!enabled || !entityIdsKey) {
            if (!enabled) {
                setSummariesById({});
            }
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        const entityIds = entityIdsKey.split('\0').filter(Boolean);

        Promise.all(entityIds.map(async (entityId) => {
            try {
                const summary = await fetchEntitySummary(entityId);
                return [entityId, summary];
            } catch {
                return [entityId, null];
            }
        }))
            .then((entries) => {
                if (cancelled) return;

                const next = {};
                for (const [entityId, summary] of entries) {
                    if (!summary) continue;
                    next[entityId] = {
                        entityType: summary.entityType || null,
                        image: summary.image || null,
                    };
                }
                setSummariesById(next);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, entityIdsKey]);

    return {summariesById, loading};
}
